import type { FunctionComponentWithRef } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import type { plusOpinionContentItemContent$key } from "scmp-app/queries/__generated__/plusOpinionContentItemContent.graphql";

import { AuthorsWrapper, Container, ContentLink, Footer, Header, Headline } from "./styles";

export type Props = ContentItemRenderVariantProps & {
  onClick?: () => void;
  reference: plusOpinionContentItemContent$key;
  withAuthor?: boolean;
};

export const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
  withAuthor,
}) => {
  const entity = useFragment(
    graphql`
      fragment plusOpinionContentItemContent on Content {
        __typename
        ...entityLink
        authors {
          __typename
        }
        ...hooksContentItemProvidersContent
          @arguments(
            withAuthorImage: true
            withAuthorLink: true
            withHeadline: true
            withActionBar: true
            withSections: true
            withSummary: true
          )
      }
    `,
    reference,
  );

  const isMultipleAuthors = (entity.authors?.length ?? 0) > 1;

  const providers = useContentItemProviders(entity);

  const { handleOpen } = usePlusDetailedListingPopup();
  const handleAuthorOnClick = useCallback(
    (entityUuid: string) => {
      handleOpen({
        entityUuid,
        type: "author",
      });
    },
    [handleOpen],
  );

  return (
    <LinkHighlightArea>
      <Container className={className} ref={ref}>
        <Header>
          {withAuthor && (
            <AuthorsWrapper $isMultipleAuthors={isMultipleAuthors}>
              {providers.authorImage({
                ignoreAuthorType: true,
                isMultiple: true,
                onClick: handleAuthorOnClick,
                withBorder: false,
                withoutLink: true,
              })}
              {providers.author({
                ignoreAuthorType: true,
                isEnableLink: false,
                isMultiple: true,
                onClick: handleAuthorOnClick,
                withAndSeparator: true,
              })}
            </AuthorsWrapper>
          )}
          <LinkHighlightArea>
            <ContentLink onClick={onClick}>
              <Headline>
                {providers.headline({
                  preferSocialHeadline: true,
                })}
              </Headline>
            </ContentLink>
          </LinkHighlightArea>
        </Header>
        <Footer>
          {providers.actionBar({
            displayDateVariant: "publishedDate",
            isBookmarkVisible: false,
            isCommentsVisible: false,
            isIconVisible: false,
          })}
        </Footer>
      </Container>
    </LinkHighlightArea>
  );
};

Component.displayName = "ContentItemPlusOpinion";
export const ContentItemPlusOpinion = withHighlightedHeadline(Component);
